import { forOwn, isPlainObject } from 'lodash-es';

import type { RequiredBy } from '@iconik-io/core/src/types';
import type {
  SearchCriteriaRangeFilterSchema,
  SearchCriteriaTermSchema,
  SearchFacetFilterSchema,
  SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';

import {
  COLLECTION_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../../constants/routes';
import type { AppState } from '../../../store/types';
import type { AnyObject } from '../../../types/common';

type RouteParamsLike = {
  route: string;
  params: Record<string, string | undefined>;
};

export const getCollectionIdFromRoute = ({
  route,
  params,
}: RouteParamsLike) => {
  if ([COLLECTION_PAGE_ROUTE, SHARE_COLLECTION_PAGE_ROUTE].includes(route)) {
    return params.id;
  }

  return undefined;
};

export const getSavedSearchIdFromRoute = ({
  route,
  params,
}: RouteParamsLike) => {
  if (route === SAVED_SEARCH_PAGE_ROUTE) {
    return params.id;
  }
  return undefined;
};

export const getRouteCollectionId = (state: AppState) =>
  getCollectionIdFromRoute(state.routeParams);

export const getRouteSavedSearchId = (state: AppState) =>
  getSavedSearchIdFromRoute(state.routeParams);

export const collectMetadataFieldNames = (
  filter: SearchSearchCriteriaSchema['filter'],
  result = new Set<string>(),
): Set<string> => {
  if (!filter) {
    return result;
  }
  for (const term of filter.terms ?? []) {
    if (term.name.startsWith('metadata.')) {
      result.add(term.name.slice('metadata.'.length));
    }
  }
  for (const subFilter of filter.filters ?? []) {
    collectMetadataFieldNames(subFilter, result);
  }
  return result;
};

export const filtersObjectToElastic = (object: AnyObject = {}) => {
  const result: SearchCriteriaTermSchema[] = [];
  // Iterate over object
  Object.entries(object).forEach(([objectKey, objectValue]) => {
    // skip empty values but not Boolean
    if (typeof objectValue !== 'boolean' && !objectValue) {
      return;
    }

    switch (objectKey) {
      case 'range':
        forOwn(
          objectValue,
          (
            rangeValue: SearchCriteriaRangeFilterSchema | string,
            rangeName: string,
          ) => {
            if (!rangeValue) {
              return; // skip empty ranges
            }
            if (typeof rangeValue === 'string') {
              result.push({
                name: rangeName,
                value: rangeValue,
              });
            } else if (rangeValue.min || rangeValue.max) {
              result.push({
                name: rangeName,
                range: rangeValue,
              });
            }
          },
        );
        break;

      case 'metadata':
        Object.entries(objectValue).forEach(
          ([metadataKey, metadataValue]: any[]) => {
            // skip empty values but not Boolean
            if (typeof metadataValue !== 'boolean' && !metadataValue) {
              return;
            }

            if (Array.isArray(metadataValue)) {
              if (metadataValue.length > 0) {
                result.push({
                  name: `metadata.${metadataKey}`,
                  value_in: metadataValue.every(isPlainObject)
                    ? metadataValue.map((v) => v.value)
                    : metadataValue,
                });
              }
            } else if (isPlainObject(metadataValue)) {
              if (metadataValue.min || metadataValue.max) {
                result.push({
                  name: `metadata.${metadataKey}`,
                  range: metadataValue,
                });
              } else {
                result.push({
                  name: `metadata.${metadataKey}`,
                  value: metadataValue,
                });
              }
            } else {
              result.push({
                name: `metadata.${metadataKey}`,
                value:
                  typeof metadataValue === 'boolean'
                    ? String(metadataValue)
                    : metadataValue,
              });
            }
          },
        );
        break;

      case 'files':
        Object.entries(objectValue).forEach(([filesKey, filesValue]: any[]) => {
          if (filesValue && filesValue.length) {
            result.push({
              name: `files.${filesKey}`,
              value_in: [...filesValue],
            });
          }
        });
        break;

      default:
        if (Array.isArray(objectValue) && objectValue.length > 0) {
          result.push({
            name: objectKey,
            value_in: [...objectValue],
          });
        }
        if (!Array.isArray(objectValue)) {
          result.push({
            name: objectKey,
            value:
              typeof objectValue === 'boolean'
                ? String(objectValue)
                : objectValue,
          });
        }
        break;
    }
  });
  return result;
};

type RequiredSearchFacetFilterSchema = RequiredBy<
  SearchFacetFilterSchema,
  'value_in'
>;

export const criteriaToFacetValues = (
  facetFilters: SearchFacetFilterSchema[],
) =>
  Object.fromEntries(
    facetFilters
      .filter((item): item is RequiredSearchFacetFilterSchema =>
        Boolean(item.value_in),
      )
      .map((item) => [item.name, item.value_in]),
  );
